import React, { useState } from "react";
import "antd/dist/antd.css";
import { Button, Drawer, Row, Col, DatePicker } from "antd";
import styles from "./DashBoardMobile.module.scss";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import moment, { Moment } from "moment";
import {
  DefaultCheckedOptionsMobile,
  DefaultDateFilterMobile,
  defaultPastDateRange,
  defaultUpcomingDateRange,
  ICheckedOptionsMobile,
  IDateFilterMobile,
} from "../../../types/TripTypes";
import {
  formatDateUtc,
  isPastDateRangeChanged,
  isUpcomingDateRangeChanged,
} from "../../../helpers/tripHelper";
import { RangeValue } from "rc-picker/lib/interface.d";
import { RangePicker_Arrow } from "../../../assets/images";
import { usePaginationCtx } from "../../../context/PaginationContext";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";

type Props = {
  openFilter: boolean;
  setOpenFilter: Function;
  setServices: Function;
  serviceType?: string;
  onFilterCount?: Function;
  currentTab?: string;
  isCalling?: string;
  upcomingTripDetails: ITripDetails[];
  pastTripDetails: ITripDetails[];
};
const DashBoardMobileFilter: React.FC<Props> = ({
  openFilter,
  setOpenFilter,
  setServices,
  serviceType,
  onFilterCount,
  currentTab,
  isCalling,
  upcomingTripDetails,
  pastTripDetails,
}) => {
  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const {
    upcomingDateRangeCtx,
    pastDateRangeCtx,
    selectedUpcomingPersonIdCtx,
    selectedPastPersonIdCtx,
    activeTabCtx,
  } = usePaginationCtx();

  const [activeTab] = activeTabCtx;
  const [upcomingDateRange, setUpcomingDateRange] = upcomingDateRangeCtx;
  const [pastDateRange, setPastDateRange] = pastDateRangeCtx;
  const [, setSelectedUpcomingPersonId] = selectedUpcomingPersonIdCtx;
  const [, setSelectedPastPersonId] = selectedPastPersonIdCtx;
  const { RangePicker } = DatePicker;

  const [checkedOptions, setCheckedOptions] = useState<ICheckedOptionsMobile>(
    DefaultCheckedOptionsMobile
  );
  const [upcomingFilterPersonId, setUpcomingFilterPersonId] = useState<
    string[]
  >([]);
  const [pastFilterPersonId, setPastFilterPersonId] = useState<string[]>([]);
  const [dateRangeFilter, setDateRangeFilter] = useState<IDateFilterMobile>(
    DefaultDateFilterMobile
  );

  const serviceTypesOptions: string[] = ["All", "Ride"];
  const serviceUsersOptions: string[] = [
    "All",
    ...serviceData.map(
      (user: { firstName: string; lastName: string }) =>
        `${user.firstName} ${user.lastName}`
    ),
  ];

  const isDateSelected = () => {
    if (serviceType === "upcomingServices") {
      if (isUpcomingDateRangeChanged(dateRangeFilter.upcomingDateRangeFilter)) {
        return 1;
      } else {
        return 0;
      }
    } else {
      if (isPastDateRangeChanged(dateRangeFilter.pastDateRangeFilter)) {
        return 1;
      } else {
        return 0;
      }
    }
  };

  const totalCheckedFilterCount: number =
    (checkedOptions.checkedTripTypes.has("All")
      ? checkedOptions.checkedTripTypes.size - 1
      : checkedOptions.checkedTripTypes.size) +
    (checkedOptions.checkedServiceUsers.has("All")
      ? checkedOptions.checkedServiceUsers.size - 1
      : checkedOptions.checkedServiceUsers.size) +
    isDateSelected();

  const handleTypesClick = (option: string): void => {
    const newCheckedOptions = new Set(checkedOptions.checkedTripTypes);

    if (option === "All") {
      newCheckedOptions.clear();
      newCheckedOptions.add("All");
    } else {
      if (checkedOptions.checkedTripTypes.has(option)) {
        if (checkedOptions.checkedTripTypes.size > 1) {
          newCheckedOptions.delete("All");
        } else {
          newCheckedOptions.add("All");
        }
        newCheckedOptions.delete(option);
      } else {
        newCheckedOptions.add(option);
        newCheckedOptions.delete("All");
        if (
          serviceTypesOptions
            .slice(1)
            .every((opt) => newCheckedOptions.has(opt))
        ) {
          newCheckedOptions.clear();
          newCheckedOptions.add("All");
        }
      }
    }
    setCheckedOptions({
      ...checkedOptions,
      checkedTripTypes: newCheckedOptions,
    });
  };

  const handleUsersClick = (option: string): void => {
    const newCheckedOptions = new Set(checkedOptions.checkedServiceUsers);

    if (option === "All") {
      newCheckedOptions.clear();
      newCheckedOptions.add("All");
    } else {
      if (checkedOptions.checkedServiceUsers.has(option)) {
        if (checkedOptions.checkedServiceUsers.size > 1) {
          newCheckedOptions.delete("All");
        } else {
          newCheckedOptions.add("All");
        }
        newCheckedOptions.delete(option);
      } else {
        newCheckedOptions.add(option);
        newCheckedOptions.delete("All");
        if (
          serviceUsersOptions
            .slice(1)
            .every((opt) => newCheckedOptions.has(opt))
        ) {
          newCheckedOptions.clear();
          newCheckedOptions.add("All");
        }
      }
    }
    setCheckedOptions({
      ...checkedOptions,
      checkedServiceUsers: newCheckedOptions,
    });
  };

  const handleApplyFilters = (): void => {
    if (checkedOptions.checkedTripTypes.has("Grocery")) {
      setServices([]);
    } else {
      handleUpdateFilterDetails(checkedOptions.checkedServiceUsers);
    }
    onFilterCount && onFilterCount(totalCheckedFilterCount);
    setOpenFilter(false);
  };

  const handleUpdateFilterDetails = (checkedUsers: Set<string>) => {
    serviceData?.forEach(
      (trip: { firstName: string; lastName: string; personId: number }) => {
        if (checkedUsers.has(`${trip.firstName} ${trip.lastName}`)) {
          serviceType === "upcomingServices"
            ? upcomingFilterPersonId.push(trip.personId.toString())
            : pastFilterPersonId.push(trip.personId.toString());
        }
      }
    );
    if (serviceType === "upcomingServices") {
      setUpcomingDateRange(dateRangeFilter.upcomingDateRangeFilter);
      setSelectedUpcomingPersonId(upcomingFilterPersonId.join(","));
      setUpcomingFilterPersonId([]);
    } else {
      setPastDateRange(dateRangeFilter.pastDateRangeFilter);
      setSelectedPastPersonId(pastFilterPersonId.join(","));
      setPastFilterPersonId([]);
    }
  };

  const handleReset = (): void => {
    if (serviceType === "upcomingServices" || currentTab === "1") {
      setServices(upcomingTripDetails);
      setSelectedUpcomingPersonId("");
      isUpcomingDateRangeChanged(upcomingDateRange) &&
        setUpcomingDateRange(defaultUpcomingDateRange);
    }
    if (
      serviceType === "pastServices" ||
      serviceType === "favoriteServices" ||
      currentTab === "2"
    ) {
      setServices(pastTripDetails);
      setSelectedPastPersonId("");
      isPastDateRangeChanged(pastDateRange) &&
        setPastDateRange(defaultPastDateRange);
    }
    setDateRangeFilter(DefaultDateFilterMobile);
    setCheckedOptions(DefaultCheckedOptionsMobile);
    onFilterCount && onFilterCount(1);
    setOpenFilter(false);
  };

  const onClose = (): void => {
    setOpenFilter(false);
  };

  const handleDateRangeChange = (
    dates: RangeValue<Moment>,
    dateStrings: string[]
  ): void => {
    if (serviceType === "upcomingServices") {
      setDateRangeFilter({
        ...dateRangeFilter,
        upcomingDateRangeFilter: {
          ...dateRangeFilter.upcomingDateRangeFilter,
          startDate: dates ? dateStrings[0] : "",
          endDate: dates ? dateStrings[1] : "",
        },
      });
    } else {
      setDateRangeFilter({
        ...dateRangeFilter,
        pastDateRangeFilter: {
          ...dateRangeFilter.pastDateRangeFilter,
          startDate: dates ? dateStrings[0] : "",
          endDate: dates ? dateStrings[1] : "",
        },
      });
    }
  };

  return (
    <>
      <Drawer
        placement="bottom"
        width={360}
        height={"auto"}
        onClose={onClose}
        visible={openFilter}
        closable={false}
      >
        <Row justify="center">
          <Col className={styles.filterHeadings}>Filter By...</Col>
        </Row>
        <Row className={styles.filterHeadingMargin}>
          <Col span={24} className={styles.filterHeadings}>
            Service Types
          </Col>
          <Col span={24} className={styles.noRipple}>
            {serviceTypesOptions.map((option) => (
              <Button
                key={option}
                onClick={() => handleTypesClick(option)}
                className={
                  checkedOptions.checkedTripTypes.has(option)
                    ? styles.filterCheckBtns
                    : styles.filterUncheckBtns
                }
              >
                {option}
              </Button>
            ))}
          </Col>
        </Row>
        {isCalling !== "serviceUsersMobile" && (
          <Row className={styles.filterHeadingMargin}>
            <Col span={24} className={styles.filterHeadings}>
              Service Users
            </Col>
            <Col span={24} className={styles.noRipple}>
              {serviceUsersOptions.map((option) => (
                <Button
                  key={option}
                  onClick={() => handleUsersClick(option)}
                  className={
                    checkedOptions.checkedServiceUsers.has(option)
                      ? styles.filterCheckBtns
                      : styles.filterUncheckBtns
                  }
                >
                  {option}
                </Button>
              ))}
            </Col>
          </Row>
        )}
        {activeTab !== "3" && (
          <Row className={styles.filterHeadingMargin}>
            <Col span={24} className={styles.filterHeadings}>
              Date Range
            </Col>
            <Col className={styles.filterRangePicker}>
              <RangePicker
                format="MM/DD/YYYY"
                value={[
                  moment(
                    formatDateUtc(
                      serviceType === "upcomingServices"
                        ? dateRangeFilter.upcomingDateRangeFilter.startDate
                        : dateRangeFilter.pastDateRangeFilter.startDate
                    )
                  ).isValid()
                    ? moment(
                        serviceType === "upcomingServices"
                          ? dateRangeFilter.upcomingDateRangeFilter.startDate
                          : dateRangeFilter.pastDateRangeFilter.startDate
                      )
                    : null,
                  moment(
                    formatDateUtc(
                      serviceType === "upcomingServices"
                        ? dateRangeFilter.upcomingDateRangeFilter.endDate
                        : dateRangeFilter.pastDateRangeFilter.endDate
                    )
                  ).isValid()
                    ? moment(
                        serviceType === "upcomingServices"
                          ? dateRangeFilter.upcomingDateRangeFilter.endDate
                          : dateRangeFilter.pastDateRangeFilter.endDate
                      )
                    : null,
                ]}
                onChange={handleDateRangeChange}
                allowClear={false}
                separator={
                  <img src={RangePicker_Arrow} alt="RangePicker_Arrow" />
                }
              />
            </Col>
          </Row>
        )}

        <Row
          className={styles.filterFooterMargin}
          justify="center"
          gutter={[10, 0]}
        >
          <Col>
            <Button onClick={handleReset} className={styles.resetFilters}>
              Reset
            </Button>
          </Col>
          <Col>
            <Button
              onClick={handleApplyFilters}
              className={styles.applyFilterBtn}
            >
              Apply Filters
            </Button>
          </Col>
        </Row>
      </Drawer>
    </>
  );
};

export default DashBoardMobileFilter;
